import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@hotjar/hotsauce';
import type { OwnedAccount } from '@hotjar/account';
import { createGlobalModal } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { GlobalModalProps } from '@hotjar/webapp/features/GlobalModal/createGlobalModal';
import type { VaultedPaymentMethod } from '@hotjar/webapp/billing/payment';

import { isZuoraEnabled } from '@hotjar/webapp/domains/billing/features';
import { YearlyUpsellOwner } from './YearlyUpsellOwner';

export const DismissCode = Object.freeze({
  changePaymentMethod: 'change_payment_method',
});

interface Props {
  account: OwnedAccount;
  goToCreateNewTicket: (currentSite: null, currentLang: string) => void;
}

interface SubmitProps {
  account: OwnedAccount;
  paymentMethod: VaultedPaymentMethod;
}

export const YearlyUpsellModal = ({
  account,
  onSubmit,
  onCancel,
  goToCreateNewTicket,
}: Props & GlobalModalProps<SubmitProps>): JSX.Element => {
  const { i18n } = useTranslation();
  const dismiss = useCallback(() => onCancel(), [onCancel]);
  const contactSupport = useCallback(
    () => goToCreateNewTicket(null, i18n?.resolvedLanguage ?? 'en'),
    [goToCreateNewTicket, i18n],
  );
  const zuoraEnabled = isZuoraEnabled(account);

  return (
    <Modal contentLabel="switch to yearly billing" isOpen onClose={dismiss}>
      <Modal.Body>
        {!zuoraEnabled && (
          <YearlyUpsellOwner
            account={account}
            onSubmit={onSubmit}
            onContactSupport={contactSupport}
          />
        )}
        {zuoraEnabled && <div style={{ height: '600px' }}>Zuora HPP placeholder</div>}
      </Modal.Body>
    </Modal>
  );
};

export const openYearlyUpsellModal = createGlobalModal<Props, SubmitProps, Error>(
  YearlyUpsellModal,
);
